import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type MutableRefObject,
  type ReactNode,
} from "react";
import type { LearnerState } from "./context";

/**
 * 受講生のいまの状態を、パネルからチャットへ渡す橋。
 *
 * チャットの hook はページが持ち、コードや実行結果はパネルが持つ。上下に離れているので、
 * パネルが「いまの状態の取り出し口」を橋に登録し、チャットは質問を送る瞬間にそれを呼ぶ。
 * 状態そのものを上へ持ち上げない。持ち上げると、コードを 1 文字打つたびにページ全体が
 * 描き直される。
 */
export type LearnerStateGetter = () => LearnerState;
export type LearnerStateBridge = MutableRefObject<LearnerStateGetter | null>;

const BridgeContext = createContext<LearnerStateBridge | null>(null);

export function useCreateLearnerStateBridge(): LearnerStateBridge {
  return useRef<LearnerStateGetter | null>(null);
}

export function LearnerStateBridgeProvider({
  bridge,
  children,
}: {
  bridge: LearnerStateBridge;
  children: ReactNode;
}) {
  return <BridgeContext.Provider value={bridge}>{children}</BridgeContext.Provider>;
}

/** 橋から取り出す。登録が無い、または取り出しに失敗したら空の状態 */
export function readLearnerState(bridge: LearnerStateBridge): LearnerState {
  try {
    return bridge.current?.() ?? {};
  } catch (error) {
    console.error("[lesson-chat] learner state getter failed", error);
    return {};
  }
}

/**
 * パネルから呼ぶ。毎回の描画で最新の getter を覚えるが、登録の付け外しは
 * 1 回だけ（描画のたびに橋を書き換えない）。
 */
export function useProvideLearnerState(getter: LearnerStateGetter): void {
  const bridge = useContext(BridgeContext);
  const latest = useRef(getter);
  latest.current = getter;

  useEffect(() => {
    if (!bridge) return;
    const registered: LearnerStateGetter = () => latest.current();
    bridge.current = registered;
    return () => {
      if (bridge.current === registered) bridge.current = null;
    };
  }, [bridge]);
}
